import React, { useEffect } from 'react';
import { Check, X } from 'lucide-react';
import SelectionOverlay from './SelectionOverlay';
import { parseElement, isInPreviewContainer } from '../../utils/visualEditor/elementParser';

/**
 * Visual Editor Wrapper
 * Wraps the preview and adds visual editing capabilities (Canvas interactions)
 */
export default function VisualEditor({ 
  children, 
  isVisualEditMode,
  selectedElement,
  hoveredElement,
  handleElementClick,
  handleElementHover,
  handleElementLeave,
  clearSelection,
  toast // Optional: Pass toast object { type: 'success'|'error', message: string } to display
}) {

  // Attach click and hover listeners to preview elements
  useEffect(() => {
    if (!isVisualEditMode) return;

    const handleClick = (event) => {
      // Check if click is inside preview container
      if (!isInPreviewContainer(event.target)) return;

      // Prevent default behavior
      event.preventDefault();
      event.stopPropagation();

      // Parse element
      const elementInfo = parseElement(event.target);
      if (elementInfo) {
        handleElementClick(event, elementInfo);
      }
    };

    const handleHover = (event) => {
      if (!isInPreviewContainer(event.target)) return;

      const elementInfo = parseElement(event.target);
      if (elementInfo) {
        handleElementHover(event, elementInfo);
      }
    };

    const handleLeave = () => {
      handleElementLeave();
    };

    // Add listeners
    document.addEventListener('click', handleClick, true);
    document.addEventListener('mouseover', handleHover, true);
    document.addEventListener('mouseout', handleLeave, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mouseover', handleHover, true);
      document.removeEventListener('mouseout', handleLeave, true);
    };
  }, [isVisualEditMode, handleElementClick, handleElementHover, handleElementLeave]);

  // Handle escape key to deselect
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        clearSelection();
      }
    };

    if (isVisualEditMode) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVisualEditMode, clearSelection]);

  return (
    <>
      {/* Selection Overlay */}
      <SelectionOverlay
        selectedElement={selectedElement}
        hoveredElement={hoveredElement}
        isVisualEditMode={isVisualEditMode}
      />

      {/* Main Content - Just pass through children without wrapper */}
      <div className={`${isVisualEditMode ? 'cursor-crosshair' : ''}`}>
        {children}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[10001] px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 transition-all"
          style={{
            backgroundColor: toast.type === 'success' ? '#10b981' : '#ef4444',
            color: 'white'
          }}
        >
          {toast.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : (
            <X className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Visual Edit Mode Indicator */}
      {isVisualEditMode && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-full shadow-lg flex items-center gap-2">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          Visual Edit Mode Active
        </div>
      )}
    </>
  );
}
